import { memo, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { TFunction } from 'i18next';
import {
  BarChart3,
  Bell,
  Calendar,
  ChevronDown,
  ChevronsUpDown,
  FolderClock,
  Folders,
  Handshake,
  HelpCircle,
  LogOut,
  LucideIcon,
  PanelLeft,
  Settings,
  Sun,
  Users,
  Wallet,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';

import LogoDark from '@/assets/svgs/logo-dark.svg';
import LogoLight from '@/assets/svgs/logo-light.svg';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarResizeHandle,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { capitalizeFirstLetter } from '@/helpers';
import { useLocalStorage } from '@/hooks/use-local-storage';
import {
  LogoutConfirmationModal,
  SupportModal,
  useFeatureFlags,
  usePermissions,
  useTheme,
} from '@/modules/common';
import { getUserProfile } from '@/modules/common/api/opsium-user.api';
import { ThemeButton } from '@/modules/common/components';
import { EntityAvatar } from '@/modules/common/components/molecules/avatar/entity-avatar';
import { DataConflictsIndicator } from '@/modules/common/components/organisms/data-conflicts-indicator';
import { IntegrationStatusCard } from '@/modules/common/components/organisms/integration-status-card';
import { useOrganizationSummary } from '@/modules/common/hooks';
import { FeatureFlags } from '@/modules/common/hooks/use-feature-flags';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { DemoBadge, DemoTrialBar } from '@/modules/demo';
import { GuideBanner } from '@/modules/guide/components/guide-fab';
import { NotificationTray, useUnreadNotificationsCount } from '@/modules/notifications';
import { ScrollableText } from '@/modules/planner/components/assignment/text/scrollable-text';
import { useAvailableSettings } from '@/modules/settings';
import { TimeTracker } from '@/modules/time-tracking/components/time-tracker/time-tracker';
import { TimeTrackerView } from '@/modules/time-tracking/types/time-tracking';
import { AuthenticatedRoutes } from '@/router/routes';
import { Themes } from '@/types/theme';

const NOTIFICATION_TRAY_STATE_STORAGE_KEY = 'notification_tray_state';
const SIDEBAR_EXPANDED_MENUS_STORAGE_KEY = 'sidebar_expanded_menus';

/** Stable identifiers for collapsible sidebar groups (used as persistence keys). */
const SidebarGroupKey = {
  Time: 'time',
  Finance: 'finance',
} as const;

type SidebarGroupKeyType = (typeof SidebarGroupKey)[keyof typeof SidebarGroupKey];

/**
 * Groups expanded by default on first load. Their state is then persisted per
 * group in localStorage, so subsequent visits restore the user's last choice.
 */
const DEFAULT_EXPANDED_MENUS: Record<string, boolean> = {
  [SidebarGroupKey.Time]: true,
  [SidebarGroupKey.Finance]: true,
};

interface SidebarSubitem {
  title: string;
  url: string;
}

interface SidebarItems {
  title: string;
  url: string;
  icon: LucideIcon;
  subItems?: SidebarSubitem[];
  /** Stable key for collapsible groups; used to persist expand/collapse state. */
  key?: SidebarGroupKeyType;
}

interface GetSidebarItemsParams {
  t: TFunction<'common', undefined>;
  hasAnyPermission: (policies: OpsiumPolicy[]) => boolean;
  featureFlags: FeatureFlags;
}

/**
 * Pushes finance-related menu items to the sidebar if the user has the necessary permissions.
 */
function pushFinanceMenuItems(
  hasAnyPermission: (policies: OpsiumPolicy[]) => boolean,
  t: TFunction<'common', undefined>,
  items: Array<SidebarItems>,
  featureFlags: FeatureFlags
): void {
  const financeSubItems: SidebarSubitem[] = [];

  if (hasAnyPermission([OpsiumPolicy.InvoicesView])) {
    financeSubItems.push({
      title: t('sidebar.finance.invoices'),
      url: AuthenticatedRoutes.Invoices,
    });
  }

  if (hasAnyPermission([OpsiumPolicy.BillsView])) {
    financeSubItems.push({
      title: t('sidebar.finance.bills'),
      url: AuthenticatedRoutes.Bills,
    });
  }

  if (featureFlags.showOverheads && hasAnyPermission([OpsiumPolicy.OverheadsView])) {
    financeSubItems.push({
      title: t('sidebar.finance.overheads'),
      url: AuthenticatedRoutes.Overheads,
    });
  }

  if (financeSubItems.length > 0) {
    items.push({
      title: t('sidebar.finance.label'),
      url: AuthenticatedRoutes.Invoices,
      icon: Wallet,
      subItems: financeSubItems,
      key: SidebarGroupKey.Finance,
    });
  }
}

function pushTimeMenuItems(
  items: Array<SidebarItems>,
  t: TFunction<'common', undefined>,
  hasAnyPermission: (policies: OpsiumPolicy[]) => boolean,
  featureFlags: FeatureFlags
) {
  const subItems: SidebarSubitem[] = [];

  if (featureFlags.showTimeTracking) {
    if (hasAnyPermission([OpsiumPolicy.WorklogsView])) {
      subItems.push({
        title: t('sidebar.time.tracking'),
        url: AuthenticatedRoutes.TimeTracking,
      });
    }

    if (hasAnyPermission([OpsiumPolicy.WorklogsApprovalsView])) {
      subItems.push({
        title: t('sidebar.time.approvals'),
        url: AuthenticatedRoutes.TimeTrackingApprovals,
      });
    }
  }

  if (featureFlags.showTimeOffs && hasAnyPermission([OpsiumPolicy.TimeOffsView])) {
    subItems.push({
      title: t('sidebar.time.timeOff'),
      url: AuthenticatedRoutes.TimeOff,
    });
  }

  if (featureFlags.showTimeOffs && hasAnyPermission([OpsiumPolicy.TimeOffsApprove])) {
    subItems.push({
      title: t('sidebar.time.timeOffApprovals'),
      url: AuthenticatedRoutes.TimeOffApprovals,
    });
  }

  if (subItems.length > 0) {
    items.push({
      title: t('sidebar.time.label'),
      url: subItems[0].url,
      icon: FolderClock,
      subItems,
      key: SidebarGroupKey.Time,
    });
  }
}

/**
 * Generates sidebar navigation items with internationalization.
 * Filters items based on user permissions.
 *
 * Permission mapping:
 * - Planner: AssignmentsViewOwn OR AssignmentsViewAll
 * - Team: TeamMembersViewAll
 * - Projects: ProjectsViewAll
 * - Clients: ClientsViewAll
 * - Finance: InvoicesAndBillsView
 * - Settings: Always shown (has own API for section visibility)
 *
 * MVP exclusions: Dashboard, Time, Reports
 */
const getSidebarItems = ({
  t,
  hasAnyPermission,
  featureFlags,
}: GetSidebarItemsParams): Array<SidebarItems> => {
  const items: Array<SidebarItems> = [];

  // Not used in MVP
  // Dashboard tab
  // {
  //   title: t('sidebar.dashboard'),
  //   url: AuthenticatedRoutes.Dashboard,
  //   icon: LayoutDashboard,
  // },

  // Time group (Tracking, Approvals, Time-offs) — each sub-item gated by its own feature flag + policy
  if (featureFlags.showTimeTracking || featureFlags.showTimeOffs) {
    pushTimeMenuItems(items, t, hasAnyPermission, featureFlags);
  }

  // Planner: View own assignments OR View all assignments
  if (hasAnyPermission([OpsiumPolicy.AssignmentsViewAll])) {
    items.push({
      title: t('sidebar.planner'),
      url: AuthenticatedRoutes.Planner,
      icon: Calendar,
    });
  }

  // Reports tab — gated by showReports feature flag
  if (featureFlags.showReports) {
    items.push({
      title: t('sidebar.reports'),
      url: AuthenticatedRoutes.Reports,
      icon: BarChart3,
    });
  }

  // Team: View all team members
  if (hasAnyPermission([OpsiumPolicy.TeamMembersViewAll])) {
    items.push({
      title: t('sidebar.team'),
      url: AuthenticatedRoutes.Team,
      icon: Users,
    });
  }

  // Projects: View all projects
  if (hasAnyPermission([OpsiumPolicy.ProjectsViewAll])) {
    items.push({
      title: t('sidebar.projects'),
      url: AuthenticatedRoutes.Projects,
      icon: Folders,
    });
  }

  // Clients: View all clients
  if (hasAnyPermission([OpsiumPolicy.ClientsViewAll])) {
    items.push({
      title: t('sidebar.clients'),
      url: AuthenticatedRoutes.Clients,
      icon: Handshake,
    });
  }

  pushFinanceMenuItems(hasAnyPermission, t, items, featureFlags);

  return items;
};

/**
 * Main application sidebar component with collapsible navigation.
 * Supports both collapsed/expanded states with different interaction patterns.
 *
 * Features:
 * - Responsive collapsible sidebar (icon mode vs expanded mode)
 * - Hierarchical navigation with sub-menus
 * - Theme switching and user profile management
 * - Tooltip support for collapsed state
 * - Mobile-optimized interactions
 *
 * @example
 * ```tsx
 * // Basic usage in layout
 * <div className="flex h-screen">
 *   <AppSidebar />
 *   <main className="flex-1">
 *     {children}
 *   </main>
 * </div>
 *
 * // With SidebarProvider for state management
 * <SidebarProvider>
 *   <AppSidebar />
 *   <SidebarInset>
 *     <main>{children}</main>
 *   </SidebarInset>
 * </SidebarProvider>
 * ```
 */

const AppSidebarComponent = () => {
  const { t } = useTranslation('common');
  const { theme, resolvedTheme } = useTheme();
  const { state, width } = useSidebar();
  const { hasAnyPermission, hasPermission } = usePermissions();
  const featureFlags = useFeatureFlags();

  const { data: availableSettings } = useAvailableSettings();
  const hasAnyAvailableSettings = availableSettings != undefined && availableSettings.length > 0;
  const location = useLocation();
  const isOnSettingsPage = location.pathname.startsWith(AuthenticatedRoutes.Settings);

  const [openUser, setOpenUser] = useState(false);
  // Manages popover state for sub-menus in collapsed sidebar mode
  const [openMenuPopovers, setOpenMenuPopovers] = useState<Record<string, boolean>>({});
  // Expanded state for sub-menus in full sidebar mode, persisted per group.
  // Time + Finance start expanded (see DEFAULT_EXPANDED_MENUS).
  const [expandedMenus, setExpandedMenus] = useLocalStorage<Record<string, boolean>>(
    SIDEBAR_EXPANDED_MENUS_STORAGE_KEY,
    DEFAULT_EXPANDED_MENUS
  );
  // Notification tray state with localStorage persistence
  const [isNotificationTrayOpen, setIsNotificationTrayOpen] = useLocalStorage(
    NOTIFICATION_TRAY_STATE_STORAGE_KEY,
    false
  );

  // Fetch unread notifications count
  const { count: unreadCount } = useUnreadNotificationsCount();

  // Fetch user profile
  const { data: userProfile } = useQuery({
    queryKey: ['user-profile'],
    queryFn: () => {
      return getUserProfile();
    },
    staleTime: 1000 * 60 * 30, // 30 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
  });

  // Fetch organization
  const { data: organization } = useOrganizationSummary();

  const sidebarItems = useMemo(
    () => getSidebarItems({ t, hasAnyPermission, featureFlags }),
    [t, hasAnyPermission, featureFlags]
  );

  /**
   * Manages popover visibility for sub-menus in collapsed sidebar state.
   */
  const toggleMenuPopover = (itemTitle: string, isOpen: boolean) => {
    setOpenMenuPopovers(prev => ({ ...prev, [itemTitle]: isOpen }));
  };

  /**
   * Toggles expanded state for sub-menus in full sidebar state. Keyed by the
   * group's stable key (falls back to title) so the persisted state survives
   * language changes.
   */
  const toggleExpandedMenu = (menuKey: string) => {
    setExpandedMenus({ ...expandedMenus, [menuKey]: !expandedMenus[menuKey] });
  };

  /**
   * Renders sidebar items with sub-menus, adapting behavior based on sidebar state.
   * Uses popovers for collapsed state, traditional expansion for full state.
   */
  const SidebarItemWithSubMenu = ({ item }: { item: SidebarItems }) => {
    const menuKey = item.key ?? item.title;
    const isMenuOpen = openMenuPopovers[item.title] || false;
    const isMenuExpanded = expandedMenus[menuKey] ?? false;

    if (state === 'collapsed') {
      return (
        <Popover open={isMenuOpen} onOpenChange={isOpen => toggleMenuPopover(item.title, isOpen)}>
          <PopoverTrigger asChild>
            <SidebarMenuButton tooltip={item.title}>
              <item.icon />
              <span>{item.title}</span>
            </SidebarMenuButton>
          </PopoverTrigger>

          <PopoverContent className="flex w-[185px] flex-col p-0" align="start" side="right">
            {item.subItems?.map(subItem => (
              <Button
                key={subItem.title}
                variant="ghost"
                size="sm"
                className="w-full justify-start font-normal"
                asChild
              >
                <NavLink to={subItem.url} onClick={() => toggleMenuPopover(item.title, false)}>
                  <span>{subItem.title}</span>
                </NavLink>
              </Button>
            ))}
          </PopoverContent>
        </Popover>
      );
    }

    return (
      <>
        <SidebarMenuButton tooltip={item.title} onClick={() => toggleExpandedMenu(menuKey)}>
          <item.icon />
          <span>{item.title}</span>
          <ChevronDown
            className={`ml-auto h-4 w-4 transition-transform ${isMenuExpanded ? 'rotate-180' : ''}`}
          />
        </SidebarMenuButton>

        {isMenuExpanded && (
          <SidebarMenuSub>
            {item.subItems?.map(subItem => (
              <SidebarMenuSubItem key={subItem.title}>
                <SidebarMenuSubButton asChild>
                  <NavLink to={subItem.url}>
                    <span>{subItem.title}</span>
                  </NavLink>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        )}
      </>
    );
  };

  const canTrackTime = hasAnyPermission([OpsiumPolicy.WorklogsCreate]);

  return (
    <>
      {/* This is the main collapsible sidebar */}
      <Sidebar collapsible="icon" className="border-r">
        <SidebarHeader>
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-2 [[data-side=left][data-state=collapsed]_&]:hidden">
              <img
                alt={t('contentDescription.productLogo')}
                src={resolvedTheme === Themes.LIGHT ? LogoDark : LogoLight}
                className="size-5 min-w-5"
              />
              <span className="text-lg/5 font-semibold text-foreground">{t('productName')}</span>
              <DemoBadge />
            </div>

            {state === 'collapsed' ? (
              <SidebarTrigger className="[[data-side=left][data-state=collapsed]_&]:hidden">
                <PanelLeft className="h-4 w-4" />
              </SidebarTrigger>
            ) : (
              <SidebarTrigger className="[[data-side=left][data-state=collapsed]_&]:hidden">
                <PanelLeft className="h-4 w-4" />
              </SidebarTrigger>
            )}

            <Tooltip>
              <TooltipTrigger asChild>
                <SidebarTrigger className="[[data-side=left][data-state=expanded]_&]:hidden">
                  <PanelLeft className="h-4 w-4" />
                </SidebarTrigger>
              </TooltipTrigger>
              <TooltipContent side="right" hidden={state !== 'collapsed'}>
                {t('sidebar.expand')}
              </TooltipContent>
            </Tooltip>
          </div>
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {sidebarItems.map(item => (
                  <SidebarMenuItem key={item.title}>
                    {item.subItems && item.subItems.length > 0 ? (
                      <SidebarItemWithSubMenu item={item} />
                    ) : (
                      <SidebarMenuButton asChild tooltip={item.title}>
                        <NavLink to={item.url}>
                          <item.icon />
                          <span>{item.title}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    )}
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter className="mb-2">
          <SidebarMenu>
            {canTrackTime && featureFlags.showTimeTracking && (
              <SidebarMenuItem className="mb-2">
                <TimeTracker
                  view={state === 'collapsed' ? TimeTrackerView.Compact : TimeTrackerView.Cascade}
                  cascadeSize={width < 200 ? 'compact' : width < 228 ? 'medium' : 'full'}
                />
              </SidebarMenuItem>
            )}

            <SidebarMenuItem className="mb-2">
              <GuideBanner isCollapsed={state === 'collapsed'} />
            </SidebarMenuItem>

            <SidebarMenuItem className="mb-2">
              <DemoTrialBar isCollapsed={state === 'collapsed'} />
            </SidebarMenuItem>

            {/* Integration Status Card - shown only for users with integration management permissions */}
            {hasAnyPermission([OpsiumPolicy.IntegrationsManage]) && (
              <SidebarMenuItem>
                <IntegrationStatusCard isCollapsed={state === 'collapsed'} />
              </SidebarMenuItem>
            )}

            {/* Users who may see data conflicts but can't resolve them get the contact info instead */}
            {hasPermission(OpsiumPolicy.IntegrationConflictsView) &&
              !hasPermission(OpsiumPolicy.IntegrationsManage) && <DataConflictsIndicator />}

            <SidebarSeparator className="mb-2" />

            {featureFlags.showNotifications && (
              <SidebarMenuItem className="cursor-pointer">
                <SidebarMenuButton
                  tooltip={t('sidebar.notifications')}
                  onClick={() => setIsNotificationTrayOpen(!isNotificationTrayOpen)}
                >
                  <div className="relative">
                    <Bell className="h-4 w-4" />
                    {unreadCount > 0 && (
                      <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-destructive-foreground">
                        {unreadCount > 99 ? t('sidebar.notifications.hundredAndMore') : unreadCount}
                      </span>
                    )}
                  </div>
                  <span>{t('sidebar.notifications')}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}

            {hasAnyAvailableSettings && (
              <SidebarMenuItem className="cursor-pointer">
                <SidebarMenuButton asChild tooltip={t('sidebar.settings')}>
                  <NavLink
                    to={AuthenticatedRoutes.Settings}
                    onClick={e => isOnSettingsPage && e.preventDefault()}
                  >
                    <Settings />
                    <span>{t('sidebar.settings')}</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}

            <SidebarMenuItem className="cursor-pointer">
              <ThemeButton>
                <SidebarMenuButton asChild tooltip={t('sidebar.appearance')}>
                  <span>
                    <Sun />
                    <span className="truncate">{t('sidebar.appearance')}</span>

                    <span className="ml-auto flex shrink-0 items-center gap-0.5">
                      {capitalizeFirstLetter(theme as string)}
                      <ChevronsUpDown className="h-4 w-4" />
                    </span>
                  </span>
                </SidebarMenuButton>
              </ThemeButton>
            </SidebarMenuItem>

            <SidebarMenuItem className="cursor-pointer">
              <Popover open={openUser} onOpenChange={setOpenUser}>
                <PopoverTrigger asChild>
                  <SidebarMenuButton asChild className="h-auto">
                    <span>
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        <EntityAvatar
                          image={userProfile?.profileImageUrl}
                          name={userProfile?.name || ''}
                          size="sm"
                          shape="rounded"
                          className="shrink-0 [[data-side=left][data-state=collapsed]_&]:-ml-2"
                          alt={userProfile?.name}
                        />

                        <div className="min-w-0">
                          <ScrollableText className="text-sm font-medium">
                            {userProfile?.name || ''}
                          </ScrollableText>
                          <ScrollableText className="text-xs">
                            {organization?.name || ''}
                          </ScrollableText>
                        </div>
                      </div>

                      <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0" />
                    </span>
                  </SidebarMenuButton>
                </PopoverTrigger>

                <PopoverContent className="flex w-[185px] flex-col p-0" align="end" side="right">
                  <SupportModal>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start font-normal focus-visible:ring-0 focus-visible:ring-offset-0"
                    >
                      <HelpCircle />
                      {t('sidebar.user.support')}
                    </Button>
                  </SupportModal>

                  <hr className="my-0.5" />

                  <LogoutConfirmationModal
                    trigger={
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start font-normal focus-visible:ring-0 focus-visible:ring-offset-0"
                      >
                        <LogOut />
                        {t('logout')}
                      </Button>
                    }
                  />
                </PopoverContent>
              </Popover>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
        <SidebarResizeHandle />
      </Sidebar>

      {/* This is the second sidebar */}
      {/* We disable collapsible and let it fill remaining space */}
      {featureFlags.showNotifications && (
        <NotificationTray
          isOpen={isNotificationTrayOpen}
          onClose={() => setIsNotificationTrayOpen(false)}
        />
      )}
    </>
  );
};

// Memoize to prevent unnecessary re-renders when parent components update
export const AppSidebar = memo(AppSidebarComponent);
